import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import classes from './GameItem.module.scss';
import NewLabel from '../../UI/NewLabel';
import BonusLabel from '../../UI/BonusLabel';
import JackpotLabel from '../../UI/JackpotLabel';
import { useJackpotsForGame } from '@homeplay/hooks';
import { useSelector } from 'react-redux';
import { selectJackpotLifecycles } from '@homeplay/app';
import lotoImg from '../../../../../shared/assets/images/loto.png';

const GameItem = ({
  gamePath,
  isNew,
  hasBonus,
  gameImg,
  imgWigth,
  imgHeight,
  alt,
  activeHappyHour,
  productId,
  slug,
  jackpotMode,
}) => {
  const [imgError, setImgError] = useState(false);
  const [isGamePopupOpen, setIsGamePopupOpen] = useState(false);
  // TODO: always empty, so the jackpot badge below is never shown (see useJackpotsForGame)
  const { jackpotsForGame } = useJackpotsForGame(slug);
  const jackpotLifecycles = useSelector(selectJackpotLifecycles);
  const hasActiveLifecycles = Object.keys(jackpotLifecycles || {}).length > 0;
  const rootRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  let gameConfig = null;

  const isJackpotPage = location.pathname.includes('/jackpot');

  const handleClickOutside = useCallback((e) => {
    if (rootRef.current && !rootRef.current.contains(e.target)) {
      setIsGamePopupOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isGamePopupOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isGamePopupOpen, handleClickOutside]);

  const handleImgLoadError = () => {
    setImgError(true);
  };

  const effectiveJackpotMode =
    jackpotMode === 'optional' && !hasActiveLifecycles
      ? 'disabled'
      : jackpotMode;

  const onGameCardClick = (e) => {
    switch (effectiveJackpotMode) {
      case 'optional':
        e.preventDefault();
        if (isJackpotPage) {
          navigate(gamePath, { state: { exitUrl: window.location.href, isJackpotEnabled: true } });
        } else {
          setIsGamePopupOpen(true);
        }
        break;
      case 'forced':
        e.preventDefault();
        navigate(gamePath, { state: { exitUrl: window.location.href, isJackpotEnabled: true } });
        break;
      case 'disabled':
      default:
        break;
    }
  };

  const onWithJackpotClick = () => {
    navigate(gamePath, { state: { exitUrl: window.location.href, isJackpotEnabled: true } });
  };

  const onWithoutJackpotClick = () => {
    navigate(gamePath, { state: { exitUrl: window.location.href, isJackpotEnabled: false } });
  };

  const parseLotoConfigs = (name) => {
    const words = name.split(' ');
    const { name: country, code } = parseLotoCountry(words);
    const lottoName = name.replace(country, '');
    const result = lottoName.match(/\d+\/\d+/g);
    const digits = result[0].split('/');
    return {
      country: country,
      code: code,
      name: lottoName,
      digit1: digits[0],
      digit2: digits[1],
    };
  };

  const parseLotoCountry = (words) => {
    const firstWord = words[0].toLowerCase();
    let name;
    let code;

    if (
      firstWord === 'united' ||
      firstWord === 'south' ||
      firstWord === 'new' ||
      firstWord === 'hong'
    ) {
      name = words[0] + ' ' + words[1];
      code = firstWord + '-' + words[1].toLowerCase();
    } else if (firstWord === 'bosnia' && words[1].toLowerCase() === 'and') {
      name = words[0] + ' ' + words[1] + ' ' + words[2];
      code =
        firstWord + '-' + words[1].toLowerCase() + '-' + words[2].toLowerCase();
    } else {
      name = words[0];
      code = firstWord;
    }

    return { name, code };
  };

  if (productId === 'oln') {
    gameConfig = parseLotoConfigs(alt);
  }

  return (
    <div className={classes.Game} ref={rootRef}>
      {isGamePopupOpen && (
        <div className={classes.GamePopup}>
          <button
            className={classes.GamePopupBtn}
            onClick={onWithJackpotClick}
          >
            With Jackpot
          </button>
          <button
            className={classes.GamePopupBtn}
            onClick={onWithoutJackpotClick}
          >
            Without Jackpot
          </button>
          <div
            className={classes.GamePopupClose}
            onClick={() => setIsGamePopupOpen(false)}
          >
            ✕
          </div>
        </div>
      )}
      <Link
        to={gamePath}
        state={{ exitUrl: window.location.href }}
        className={classes.GameLink}
        onClick={onGameCardClick}
      >
        {hasBonus && activeHappyHour && <BonusLabel />}
        {jackpotsForGame?.length > 0 && (
          <div
            className={classes.JackpotLabel}
            data-new={isNew ? '1' : '0'}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <JackpotLabel slug={slug} />
          </div>
        )}
        {isNew && <NewLabel />}

        {productId === 'oln' ? (
          <React.Fragment>
            {gameConfig && (
              <div className={classes.Loto}>
                <div
                  className={classes.LotoFlag}
                  style={{
                    backgroundImage: `url(/static/media/${gameConfig.code}.jpg)`,
                  }}
                ></div>
                <img src={lotoImg} alt={alt} width={264} height={158} />
                <h5>
                  <span
                    style={{
                      fontSize: `${Math.min(100, 1800 / gameConfig.country.length)}%`,
                    }}
                  >
                    {gameConfig.country}
                  </span>{' '}
                  <span
                    style={{
                      fontSize: `${gameConfig.name.length < 32 ? Math.min(100, 2100 / gameConfig.name.length) : 70}%`,
                    }}
                  >
                    {gameConfig.name}
                  </span>
                </h5>
                <div className={classes.Digit1}>{gameConfig.digit1}</div>
                <div className={classes.Digit2}>{gameConfig.digit2}</div>
              </div>
            )}
          </React.Fragment>
        ) : (
          <React.Fragment>
            {imgError ? (
              <div
                className="placeholder"
                style={{
                  width: `${imgWigth}px`,
                  paddingBottom: `${(imgHeight / imgWigth) * 100}%`,
                }}
              >
                <span>{alt}</span>
              </div>
            ) : (
              <img
                src={gameImg}
                alt={alt}
                width={imgWigth}
                height={imgHeight}
                onError={handleImgLoadError}
              />
            )}
          </React.Fragment>
        )}
      </Link>
    </div>
  );
};

export default GameItem;
